/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T21:10:00+05:00
Intent: Card headers follow the panel language, matched by title or by the gateway event.
*/
import { SharedService } from './../../../services/shared.service';
import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { NotificationsService } from '@services/notifications.service';
import { PagesService } from '@services/pages.service';
import { SharedModule } from '@shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { localizedNotificationTitle, messageWithoutReason } from '../../../utils/notification-text.util';

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
})
export class NotificationComponent {
  notif: any[] = [];
  currentPage = 1;
  pageSize = 6;
  totalPages = 1;
  activeTab: 'all' | 'read' | 'unread' = 'all'; // ✅ NEW: Track active tab

  sharedService = inject(SharedService);
  pagesService = inject(PagesService);
  notificationsService = inject(NotificationsService);
  translateService = inject(TranslateService);

  constructor(private router: Router) { }

  ngOnInit(): void {
    this.getNotificationList();

    // ✅ Subscribe to service notif array for real-time updates
    this.subscribeToRealTimeNotifications();
  }

  subscribeToRealTimeNotifications() {
    // Update local list when service list changes
    setInterval(() => {
      if (this.notificationsService.notif.length !== this.notif.length) {
        this.notif = [...this.notificationsService.notif];
        this.updateTotalPages(); // ✅ UPDATED: Use new method
      }
    }, 1000); // Check every second
  }

  // ✅ NEW: Method to update total pages based on filtered results
  updateTotalPages() {
    this.totalPages = Math.max(1, Math.ceil(this.filteredNotifications.length / this.pageSize));
  }

  async getNotificationList() {
    this.sharedService.showLoader();
    // Use the service method which properly merges real-time + API notifications
    await this.notificationsService.getNotificationList();
    this.notif = [...this.notificationsService.notif];
    this.updateTotalPages();
    this.sharedService.hideLoader();
  }

  /** Nothing to mark when there are no updates, or every one is already read. */
  get hasUnread(): boolean {
    return this.notif.some((item) => !item._notification_logs_of_notifications?.is_read);
  }

  async markAllAsRead() {
    // Get all unread notification IDs
    const unreadNotifications = this.notif.filter(
      (item) => !item._notification_logs_of_notifications?.is_read
    );

    if (unreadNotifications.length === 0) {
      return;
    }

    const notificationIds = unreadNotifications.map((item) => item.id);

    // Single bulk API call
    const success = await this.notificationsService.markAllNotificationsRead(notificationIds);

    if (success) {
      // Update UI for all unread notifications
      unreadNotifications.forEach((item) => {
        if (!item._notification_logs_of_notifications) {
          item._notification_logs_of_notifications = {
            id: 0,
            notification_id: item.id,
            is_read: true,
            is_delivered: true,
            read_at: Date.now(),
            delivered_at: Date.now(),
          };
        } else {
          item._notification_logs_of_notifications.is_read = true;
          item._notification_logs_of_notifications.read_at = Date.now();
        }
      });

      // Reset counter
      this.notificationsService.totalNewNotifications.next(0);
    }
  }

  markAsRead(item: any) {
    // If _notification_logs_of_notifications doesn't exist, create it
    if (!item._notification_logs_of_notifications) {
      const payload = {
        id: 0,
        notification_id: item.id,
        is_read: true,
        is_delivered: true,
        read_at: Date.now(),
        delivered_at: Date.now(),
      };

      // Create the object for UI update
      item._notification_logs_of_notifications = { ...payload };

      // 🔹 Counter update turant (only if it was unread)
      this.notificationsService.totalNewNotifications.next(
        Math.max(0, this.notificationsService.totalNewNotifications.value - 1)
      );

      this.notificationsService.updateNotification(payload);
      return;
    }

    // If already read, don't do anything
    if (item._notification_logs_of_notifications.is_read) {
      return;
    }

    // Mark as read
    const payload = {
      ...item._notification_logs_of_notifications,
      is_read: true,
      read_at: Date.now(),
    };

    item._notification_logs_of_notifications.is_read = true;

    // 🔹 Counter update turant
    this.notificationsService.totalNewNotifications.next(
      Math.max(0, this.notificationsService.totalNewNotifications.value - 1)
    );

    this.notificationsService.updateNotification(payload);
  }

  markAsUnread(item: any) {

    if (item._notification_logs_of_notifications?.is_read) {
      const payload = {
        ...item._notification_logs_of_notifications,
        is_read: false,
        read_at: null,
      };
      item._notification_logs_of_notifications.is_read = false;

      this.notificationsService.totalNewNotifications.next(
        this.notificationsService.totalNewNotifications.value + 1
      );
      this.notificationsService.updateNotification(payload);
    }
  }

  private unitLabel(unit: string, count: number): string {
    return this.translateService.instant(`notification.unit-${unit}${count > 1 ? 's' : ''}`);
  }

  private formatAgo(count: number, unit: string): string {
    return this.translateService.instant('notification.time-ago-single', {
      count,
      unit: this.unitLabel(unit, count),
    });
  }

  private formatAgoDouble(count1: number, unit1: string, count2: number, unit2: string): string {
    return this.translateService.instant('notification.time-ago-double', {
      count1,
      unit1: this.unitLabel(unit1, count1),
      count2,
      unit2: this.unitLabel(unit2, count2),
    });
  }

  getTimeAgo(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    const months = Math.floor(days / 30);
    const remainingWeeks = Math.floor((days % 30) / 7);
    const years = Math.floor(days / 365);
    const remainingMonths = Math.floor((days % 365) / 30);

    if (seconds < 60) return this.translateService.instant('notification.just_now');
    if (minutes < 60) return this.formatAgo(minutes, 'minute');
    if (hours < 24) return this.formatAgo(hours, 'hour');

    // 👇 handle days and weeks together
    if (days < 30) {
      if (days === 1) return this.translateService.instant('notification.yesterday');
      if (weeks >= 1 && remainingDays > 0) {
        return this.formatAgoDouble(weeks, 'week', remainingDays, 'day');
      }
      if (weeks >= 1) return this.formatAgo(weeks, 'week');
      return this.formatAgo(days, 'day');
    }

    // 👇 handle months and weeks
    if (days < 365) {
      if (months >= 1 && remainingWeeks > 0) {
        return this.formatAgoDouble(months, 'month', remainingWeeks, 'week');
      }
      return this.formatAgo(months, 'month');
    }

    // 👇 handle years and months
    if (years >= 1 && remainingMonths > 0) {
      return this.formatAgoDouble(years, 'year', remainingMonths, 'month');
    }

    return this.formatAgo(years, 'year');
  }

  // ✅ NEW: Get filtered notifications based on active tab
  get filteredNotifications() {
    switch (this.activeTab) {
      case 'read':
        return this.notif.filter(item => item._notification_logs_of_notifications?.is_read);
      case 'unread':
        return this.notif.filter(item => !item._notification_logs_of_notifications?.is_read);
      default:
        return this.notif;
    }
  }

  // ✅ UPDATED: Get notifications for current page from filtered results
  get pagedNotifications() {
    const filtered = this.filteredNotifications;
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return filtered.slice(start, end);
  }

  // ✅ NEW: Method to change active tab
  setActiveTab(tab: 'all' | 'read' | 'unread') {
    this.activeTab = tab;
    this.currentPage = 1; // Reset to first page when changing tabs
    this.updateTotalPages();
  }

  goToPage(page: any) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  nextPage() {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  prevPage() {
    if (this.currentPage > 1) this.currentPage--;
  }

  // 🔹 NEW METHOD: Smart pagination display
  getPageNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    const maxVisible = 5; // Maximum visible page numbers
    const total = Math.max(1, this.totalPages);

    if (total <= maxVisible + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (this.currentPage <= 3) {
        // Near start: 1 2 3 4 ... last
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(total);
      } else if (this.currentPage >= total - 2) {
        // Near end: 1 ... last-3 last-2 last-1 last
        pages.push('...');
        for (let i = total - 3; i <= total; i++) {
          pages.push(i);
        }
      } else {
        // Middle: 1 ... current-1 current current+1 ... last
        pages.push('...');
        pages.push(this.currentPage - 1);
        pages.push(this.currentPage);
        pages.push(this.currentPage + 1);
        pages.push('...');
        pages.push(total);
      }
    }

    return pages;
  }

  goToContestDetail(item: any) {
    if (!item?.contest_id) return;

    const uid = uuidv4();
    localStorage.setItem(uid, item.contest_id.toString());

    this.router.navigate(['/home/campaigns/detail'], {
      queryParams: {
        id: uid
      }
    });
  }

  isRejectionNotification(item: any): boolean {
    if (!item) return false;
    const name = String(item.notification_name || item.type || '').toLowerCase();
    const title = String(item.notification_title || '').toLowerCase();
    return name.includes('reject') || title.includes('reject') || title.includes('مرفوض') || !!this.getRejectionReason(item);
  }

  // An admin changed the client's plan — its "Reason:" is the overriding reason, not a rejection.
  isPlanOverrideNotification(item: any): boolean {
    if (!item) return false;
    if (item.event === 'plan_overridden') return true;
    const title = String(item.notification_title || '').toLowerCase();
    return title.includes('plan has been updated') || title.includes('تم تحديث خطتك');
  }

  /** Card header in the panel language, with the old "contest" wording replaced. */
  displayTitle(item: any): string {
    return localizedNotificationTitle(item, (key) => this.translateService.instant(key));
  }

  /** The reason has its own box under the message, so it is not repeated inside the message. */
  displayMessage(item: any): string {
    const message = item?.message || '';
    return this.getRejectionReason(item) ? messageWithoutReason(message) : message;
  }

  getRejectionReason(item: any): string | null {
    if (!item) return null;
    const reason = item.rejection_reason || item.reason || item.reject_reason || item.data?.rejection_reason || item.data?.reason || item.metadata?.rejection_reason || item.metadata?.reason;
    if (reason && typeof reason === 'string' && reason.trim()) {
      return reason.trim();
    }
    // Also check if reason is in message e.g. "Reason: <reason>" or "سبب الرفض: <reason>"
    const message = item.message || '';
    const match = message.match(/(?:reason\s*:|سبب الرفض\s*:|سبب\s*:)\s*(.+)/i);
    if (match && match[1]) {
      return match[1].trim();
    }
    return null;
  }
}